/**
 * Cost Linking Panel — UI for mapping 3D objects to CBS cost codes
 * Mirrors LinkingPanel.js from 4D but for cost code assignment
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { authFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Link2, Unlink, Search, Loader2, CheckSquare, Square, Download,
  Upload, FileSpreadsheet, Layers, Box, ChevronRight, ChevronDown,
  FolderOpen, FolderClosed, DollarSign, Key
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function LinkTreeNode({ node, depth, expandedNodes, onToggle, checkedDbIds, onCheck }) {
  const isExpanded = expandedNodes.has(node.dbId);
  const hasChildren = node.children && node.children.length > 0;
  const isChecked = checkedDbIds.has(node.dbId);

  return (
    <div>
      <div
        className="flex items-center gap-0.5 text-xs py-0.5 hover:bg-accent rounded transition-colors"
        style={{ paddingLeft: `${depth * 12 + 2}px` }}
      >
        {hasChildren ? (
          <button onClick={() => onToggle(node.dbId)} className="shrink-0 w-4 h-4 flex items-center justify-center">
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : <span className="w-4" />}
        <button onClick={() => onCheck(node.dbId)} className="shrink-0 w-4 h-4 flex items-center justify-center" data-testid={`cost-link-check-${node.dbId}`}>
          {isChecked ? <CheckSquare className="w-3.5 h-3.5 text-cyan-400" /> : <Square className="w-3.5 h-3.5 text-muted-foreground" />}
        </button>
        {hasChildren
          ? (isExpanded ? <FolderOpen className="w-3 h-3 text-amber-500 shrink-0" /> : <FolderClosed className="w-3 h-3 text-amber-500/70 shrink-0" />)
          : <Box className="w-3 h-3 text-slate-500 shrink-0" />
        }
        <span className="truncate ml-0.5 cursor-pointer" onClick={() => onCheck(node.dbId)}>{node.name || `#${node.dbId}`}</span>
      </div>
      {isExpanded && hasChildren && node.children.map(c => (
        <LinkTreeNode key={c.dbId} node={c} depth={depth + 1} expandedNodes={expandedNodes} onToggle={onToggle} checkedDbIds={checkedDbIds} onCheck={onCheck} />
      ))}
    </div>
  );
}

export function CostLinkingPanel({
  modelId, mappings, objectTreeRoots, flatObjectTreeLength,
  expandedTreeNodes, onToggle, onRefreshMappings, projectId
}) {
  const PROJECT_ID = projectId;
  const [selectedCostCode, setSelectedCostCode] = useState('');
  const [checkedDbIds, setCheckedDbIds] = useState(new Set());
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [treeSearch, setTreeSearch] = useState('');
  // Property-key selector + Excel Import/Export for 1:1 parity with 4D LinkingPanel.
  const [propertyKey, setPropertyKey] = useState('Item Number');
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [importingMapping, setImportingMapping] = useState(false);
  const [unlinkingAll, setUnlinkingAll] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!modelId) return;
    authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${modelId}/property-keys`)
      .then(r => (r.ok ? r.json() : { keys: [] }))
      .then(d => setPropertyOptions(d.keys || d.propertyKeys || []))
      .catch(() => setPropertyOptions([]));
  }, [modelId, PROJECT_ID]);

  const allPropertyOptions = (propertyOptions.length ? propertyOptions : [
    'Item Number', 'ItemNumber', 'Name', 'Family Type', 'Category', 'Level', 'Type Name', 'Mark',
  ]).map(k => (typeof k === 'string' ? { value: k, label: k } : k));

  const handleTemplate = () => {
    if (!modelId) return;
    const a = document.createElement('a');
    a.href = `${API}/api/v1/projects/${PROJECT_ID}/5d/models/${modelId}/mappings/template?property_key=${encodeURIComponent(propertyKey)}`;
    a.download = '5D_Cost_Template.xlsx';
    a.click();
    toast.success(`Template download started (key: ${propertyKey})`);
  };
  const handleExport = () => {
    if (!modelId) return;
    const a = document.createElement('a');
    a.href = `${API}/api/v1/projects/${PROJECT_ID}/5d/models/${modelId}/mappings/export?property_key=${encodeURIComponent(propertyKey)}`;
    a.download = '5D_Cost_Mappings.xlsx';
    a.click();
    toast.success(`Export download started (key: ${propertyKey})`);
  };
  const handleImport = async (file) => {
    if (!file || !modelId) return;
    setImportingMapping(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${modelId}/mappings/import?property_key=${encodeURIComponent(propertyKey)}`,
        { method: 'POST', body: fd });
      const d = await r.json();
      if (r.ok) { toast.success(`Imported: ${d.linked || d.inserted || 0} objects linked`); onRefreshMappings(); }
      else toast.error(d.detail || 'Import failed');
    } catch (e) { toast.error(e.message); }
    setImportingMapping(false);
  };
  const handleUnlinkAll = async () => {
    if (!modelId) return;
    const total = mappings.reduce((s, m) => s + (m.modelObjects?.length || 0), 0);
    if (!total) { toast.info('No objects currently linked'); return; }
    if (!window.confirm(`Unlink ALL ${total} objects from ALL cost codes? This cannot be undone.`)) return;
    setUnlinkingAll(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${modelId}/mappings/unlink-all`, { method: 'DELETE' });
      const d = await r.json();
      if (r.ok) { toast.success(`Unlinked all objects from ${d.mappingsUpdated || d.costCodesCleared || 0} cost codes`); onRefreshMappings(); }
      else toast.error(d.detail || 'Unlink All failed');
    } catch (e) { toast.error(e.message); }
    setUnlinkingAll(false);
  };

  const costCodesWithData = mappings.filter(m => m.costCode);
  const selectedMapping = mappings.find(m => m.costCode === selectedCostCode);

  // Aggregate objects across all descendants — so when the user picks a summary
  // WBS like "1 Stabilizer Plant" they see every object linked under it, not
  // just the ones stapled to code "1" itself. Matches the CostGrid rollup.
  const linkedObjects = (() => {
    if (!selectedMapping) return [];
    // Direct
    const collected = [...(selectedMapping.modelObjects || [])];
    // Descendants — a code is a descendant of another if it starts with `<code>.`
    const prefix = selectedCostCode + '.';
    for (const m of mappings) {
      if (m.costCode !== selectedCostCode && m.costCode.startsWith(prefix)) {
        for (const o of (m.modelObjects || [])) collected.push({ ...o, _viaCostCode: m.costCode });
      }
    }
    // Dedupe by dbId
    const seen = new Set(); const out = [];
    for (const o of collected) { if (!seen.has(o.dbId)) { seen.add(o.dbId); out.push(o); } }
    return out;
  })();

  const handleCheck = useCallback((dbId) => {
    setCheckedDbIds(prev => {
      const next = new Set(prev);
      if (next.has(dbId)) next.delete(dbId); else next.add(dbId);
      return next;
    });
  }, []);

  const handleLink = async () => {
    if (!selectedCostCode || checkedDbIds.size === 0) {
      toast.error('Select a cost code and at least one object');
      return;
    }
    setLinking(true);
    try {
      const resp = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${modelId}/mappings/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ costCode: selectedCostCode, dbIds: Array.from(checkedDbIds) }),
      });
      const data = await resp.json();
      if (resp.ok) {
        toast.success(`Linked ${data.objectsAdded} objects to ${selectedCostCode}`);
        setCheckedDbIds(new Set());
        onRefreshMappings();
      } else {
        toast.error(data.detail || 'Link failed');
      }
    } catch (err) {
      toast.error(err.message);
    }
    setLinking(false);
  };

  const handleUnlink = async (dbId) => {
    if (!selectedCostCode) return;
    setUnlinking(true);
    try {
      const resp = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${modelId}/mappings/${selectedCostCode}/objects/${dbId}`, { method: 'DELETE' });
      if (resp.ok) { toast.success(`Unlinked object #${dbId}`); onRefreshMappings(); }
      else { const d = await resp.json(); toast.error(d.detail || 'Unlink failed'); }
    } catch (e) { toast.error(e.message); }
    setUnlinking(false);
  };

  const handleUnlinkAllForCode = async () => {
    if (!selectedCostCode || !linkedObjects.length) return;
    if (!window.confirm(`Unlink all ${linkedObjects.length} objects from ${selectedCostCode}?`)) return;
    setUnlinking(true);
    try {
      // Loop single-object deletes since the backend exposes DELETE /mappings/{code}/objects/{dbId}.
      await Promise.all(linkedObjects.map(o =>
        authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${modelId}/mappings/${selectedCostCode}/objects/${o.dbId}`, { method: 'DELETE' })
      ));
      toast.success(`Unlinked all objects from ${selectedCostCode}`);
      onRefreshMappings();
    } catch (e) { toast.error(e.message); }
    setUnlinking(false);
  };

  const filteredRoots = treeSearch
    ? objectTreeRoots.filter(function filterTree(node) {
        if (node.name?.toLowerCase().includes(treeSearch.toLowerCase())) return true;
        return node.children?.some(filterTree);
      })
    : objectTreeRoots;

  const alreadyMapped = new Set();
  for (const m of mappings) {
    for (const o of (m.modelObjects || [])) alreadyMapped.add(o.dbId);
  }

  return (
    <div className="flex flex-col h-full" data-testid="cost-linking-panel">
      {/* Linking Controls */}
      <div className="p-2 border-b space-y-2 shrink-0">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-cyan-500" />
          <span className="text-xs font-semibold">Cost Code Linking</span>
          <Badge variant="outline" className="text-[10px] h-5 ml-auto">{checkedDbIds.size} selected</Badge>
        </div>
        <Select value={selectedCostCode} onValueChange={setSelectedCostCode}>
          <SelectTrigger className="h-7 text-xs" data-testid="cost-code-selector">
            <SelectValue placeholder="Select CBS Cost Code..." />
          </SelectTrigger>
          <SelectContent className="max-h-60">
            {costCodesWithData.map(cc => (
              <SelectItem key={cc.costCode} value={cc.costCode}>
                <span className="font-mono mr-2">{cc.costCode}</span>
                <span className="text-muted-foreground">{cc.costCodeName}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" className="w-full h-7 text-xs gap-1" onClick={handleLink} disabled={linking || !selectedCostCode || checkedDbIds.size === 0} data-testid="link-objects-btn">
          {linking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
          Link {checkedDbIds.size} Object{checkedDbIds.size !== 1 ? 's' : ''} to Cost Code
        </Button>

        {/* Row 2: Property key + Excel Template/Export/Import + Unlink All (parity with 4D LinkingPanel) */}
        <div className="flex items-center gap-1 pt-1" data-testid="cost-linking-toolbar-row2">
          <Key className="w-3 h-3 text-muted-foreground shrink-0" />
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">Map by:</span>
          <Select value={propertyKey} onValueChange={setPropertyKey}>
            <SelectTrigger className="h-6 min-w-[130px] max-w-[220px] text-[11px]" data-testid="cost-property-key-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {allPropertyOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex-1" />
          <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5 gap-0.5" onClick={handleTemplate} title={`Download template (${propertyKey})`} data-testid="cost-template-btn">
            <FileSpreadsheet className="w-3 h-3" />Tmpl
          </Button>
          <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5 gap-0.5" onClick={handleExport} title={`Export mappings (${propertyKey})`} data-testid="cost-export-btn">
            <Download className="w-3 h-3" />Export
          </Button>
          <input type="file" accept=".xlsx,.xls" className="hidden" ref={fileRef}
            onChange={e => { handleImport(e.target.files?.[0]); e.target.value = ''; }} />
          <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5 gap-0.5" onClick={() => fileRef.current?.click()} disabled={importingMapping} title={`Import mappings (${propertyKey})`} data-testid="cost-import-btn">
            {importingMapping ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}Import
          </Button>
          <div className="w-px h-4 bg-border mx-0.5" />
          <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5 gap-0.5 text-red-500 hover:text-red-400 hover:bg-red-500/10" onClick={handleUnlinkAll} disabled={unlinkingAll} title="Unlink all objects from all cost codes" data-testid="cost-unlink-all-btn">
            {unlinkingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3" />}Unlink All
          </Button>
        </div>

        {/* Currently linked objects for the selected cost code */}
        {selectedCostCode && linkedObjects.length > 0 && (
          <div className="rounded border px-2 py-1.5 space-y-1" data-testid="cost-linked-objects" style={{ backgroundColor: 'var(--table-header-bg)', borderColor: 'var(--border-color)' }}>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                Currently linked
              </span>
              <Badge variant="outline" className="text-[10px] h-4 px-1">{linkedObjects.length}</Badge>
              <button
                type="button"
                onClick={handleUnlinkAllForCode}
                disabled={unlinking}
                className="ml-auto text-[10px] text-red-400 hover:text-red-300 inline-flex items-center gap-1"
                data-testid="cost-unlink-all-for-code-btn"
              >
                <Unlink className="w-3 h-3" /> Unlink all
              </button>
            </div>
            <div className="max-h-24 overflow-y-auto space-y-0.5 pr-1">
              {linkedObjects.slice(0, 30).map(o => (
                <div key={o.dbId} className="flex items-center gap-1 text-[11px]" data-testid={`cost-linked-${o.dbId}`}>
                  <Box className="w-3 h-3 text-slate-400 shrink-0" />
                  <span className="truncate flex-1">{o.name || `#${o.dbId}`}</span>
                  <button type="button" onClick={() => handleUnlink(o.dbId)} disabled={unlinking}
                    className="text-slate-400 hover:text-red-400" data-testid={`cost-unlink-${o.dbId}`}>
                    <Unlink className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {linkedObjects.length > 30 && (
                <div className="text-[10px] text-center pt-1" style={{ color: 'var(--text-muted)' }}>
                  +{linkedObjects.length - 30} more…
                </div>
              )}
            </div>
          </div>
        )}
        {selectedCostCode && linkedObjects.length === 0 && (
          <div className="text-[11px] italic text-center py-1" style={{ color: 'var(--text-muted)' }} data-testid="cost-no-linked-objects">
            No objects linked to this cost code yet
          </div>
        )}
      </div>

      {/* Object Tree */}
      <div className="p-2 border-b shrink-0">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
          <Input
            placeholder="Search objects..."
            value={treeSearch}
            onChange={e => setTreeSearch(e.target.value)}
            className="h-7 pl-7 text-xs"
            data-testid="cost-link-tree-search"
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto p-1" data-testid="cost-link-tree">
        {flatObjectTreeLength === 0 ? (
          <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
            No objects loaded. Switch to Object Tree tab first.
          </div>
        ) : (
          filteredRoots.map(root => (
            <LinkTreeNode
              key={root.dbId}
              node={root}
              depth={0}
              expandedNodes={expandedTreeNodes}
              onToggle={onToggle}
              checkedDbIds={checkedDbIds}
              onCheck={handleCheck}
            />
          ))
        )}
      </div>
    </div>
  );
}
